import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { defineBoot } from '@quasar/app-vite/wrappers';
import { Platform } from 'quasar';
import { SafeAreas } from 'src/cap-plugins/SafeAreas';
import { onFullscreenChange } from 'src/services/fullscreen';
import store from 'src/store';

export class SafeAreaInsetHandler {
  static config = Object.freeze({
    left: Object.freeze({
      className: 'notch-left',
      cssVariable: 'sal',
      location: 'left',
    }),
    right: Object.freeze({
      className: 'notch-right',
      cssVariable: 'sar',
      location: 'right',
    }),
    top: Object.freeze({
      className: 'notch-top',
      cssVariable: 'sat',
      location: 'top',
    }),
    bottom: Object.freeze({
      className: 'notch-bottom',
      cssVariable: 'sab',
      location: 'bottom',
    }),
  });

  /**
   * Adds listeners that trigger safe area inset updates.
   *
   * @param {Vuex.Store} store
   */
  static init(store) {
    if (!Capacitor.isPluginAvailable('SafeAreas')) {
      this.updateCssVars();
      return;
    }

    this.update(store);

    document.addEventListener('deviceready', async () => {
      this.update(store);
    });

    ScreenOrientation.addListener('screenOrientationChange', () => {
      this.update(store);
    });

    onFullscreenChange(() => {
      this.update(store);
    });
  }

  /**
   * Updates the orientation, notch sizes, and notch-related classes.
   *
   * @param {Vuex.Store} store
   */
  static async update(store) {
    const safeAreas = await SafeAreas.get();

    this.updateCssVars(safeAreas);

    store.dispatch('app/setNotchSizes', safeAreas);

    // Updated notch classes
    Object.values(SafeAreaInsetHandler.config).forEach(({ className, location }) => {
      const addOrRemove = safeAreas[location] ? 'add' : 'remove';
      document.body.classList[addOrRemove](`mobile-${className}`);
      if (Platform.is.android) {
        document.body.classList[addOrRemove](`android-${className}`);
      }
      if (Platform.is.ios) {
        document.body.classList[addOrRemove](`ios-${className}`);
      }
    });
  }

  static updateCssVars({ bottom, left, right, top } = {}) {
    let styleTag = document.getElementById('safeAreaInsets');

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.type = 'text/css';
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = `
      :root {
        --safe-area-bottom: ${bottom !== undefined ? `${bottom}px` : 'env(safe-area-inset-bottom)'};
        --safe-area-left: ${left !== undefined ? `${left}px` : 'env(safe-area-inset-left)'};
        --safe-area-right: ${right !== undefined ? `${right}px` : 'env(safe-area-inset-right)'};
        --safe-area-top: ${top !== undefined ? `${top}px` : 'env(safe-area-inset-top)'};
      }
    `;
  }
}

/**
 * Initializes the SafeAreaInsetHandler
 */
export default defineBoot(() => {
  SafeAreaInsetHandler.init(store());
});
